import api from '../../service/api'

/*
	The core boxart game state: the 16-guess reveal mechanic.

	MECHANIC (per the plan's Global Constraints):
	- 4x4 = 16 tiles, 16 guesses, ONE tile revealed per WRONG guess.
	- The first tile is shown at the start, so `revealed` starts at 1.
	- Hints fire from the SERVER on the 2nd / 6th / 10th / 14th wrong guess
	  (BGG rank -> year -> themes -> designer). We never compute hint text
	  client-side: each guess response may carry a `hint` object we just store.
	- Solve = fewest tiles revealed; the server returns the final result
	  (solved/lost + the answer) and we lock the board.

	Progress is persisted per date in localStorage (`boxart.play.{date}`) so a
	reload resumes the in-progress game. Practice (a past date) persists locally
	too but is unranked: we never block the UI on a server result for it.
*/

var KEY_PREFIX = 'boxart.play.'
var MAX_TILES = 16

function storeKey(date) {

	return KEY_PREFIX + date

}

// clamp a tile count into the valid 1..16 range
function clampTiles(value) {

	value = parseInt(value, 10)

	if (!value || value < 1) return 1

	return Math.min(value, MAX_TILES)

}

// the durable slice of play state — enough to resume an in-progress game
function snapshot(state) {

	return {
		picks: state.picks,
		revealed: state.revealed,
		hints: state.hints,
		result: state.result,
		practice: state.practice,
		// opaque puzzle-identity token; lets a reload detect a re-curated date
		version: state.version
	}

}

function persist(state) {

	if (!state.date) return

	try {

		localStorage.setItem(storeKey(state.date), JSON.stringify(snapshot(state)))

	} catch (e) { /* storage full / disabled — play continues in-memory */ }

}

function restore(date) {

	try {

		var saved = JSON.parse(localStorage.getItem(storeKey(date)))

		return saved || null

	} catch (e) {

		return null

	}

}

export default {

	namespaced: true,

	state: {
		// the today/puzzle payload from the server
		today: null,
		// the puzzle date this state belongs to (keys local persistence)
		date: null,
		// guessed game ids, in the order they were tried
		picks: [],
		// tiles shown; the first is visible from the start
		revealed: 1,
		// unlocked hint objects, in arrival order (server-authored)
		hints: [],
		// set when the game finishes: { solved, answer, score, ... }
		result: null,
		// opaque puzzle-identity token from the descriptor; a changed token means
		// the date was re-curated, so any saved local snapshot is discarded
		version: null,
		// a past-date replay — unranked, resolved locally
		practice: false,
		// in-flight flag for load()/guess()
		loading: false
	},

	getters: {

		today: function(state) {

			return state.today

		},

		tilesRevealed: function(state) {

			return state.revealed

		},

		guessesUsed: function(state) {

			return state.picks.length

		},

		remaining: function(state) {

			return MAX_TILES - state.picks.length

		},

		finished: function(state) {

			return !!state.result

		},

		// wrong guesses so far (every pick but a final solving one). Mirrors the
		// reveal count growth: revealed = 1 + wrongCount (capped at 16).
		wrongCount: function(state) {

			return Math.max(0, state.revealed - 1)

		},

		hints: function(state) {

			return state.hints

		},

		result: function(state) {

			return state.result

		},

		loading: function(state) {

			return state.loading

		}

	},

	mutations: {

		loading: function(state, value) {

			state.loading = !!value

		},

		// drop the current puzzle so the loader shows while a new one loads
		// (a guess never calls this — only load() does)
		clear: function(state) {

			state.today = null
			state.date = null
			state.picks = []
			state.revealed = 1
			state.hints = []
			state.result = null
			state.version = null
			state.practice = false

		},

		// seed a fresh game (no saved progress)
		init: function(state, data) {

			data = data || {}

			var locked = data.result || null

			state.today = data.today || null
			state.date = data.date || null
			state.practice = !!data.practice
			state.version = (data.today && data.today.version) || null
			state.hints = []
			state.result = locked

			// a server-locked (already finished) board has no local progress to
			// resume — seed the real tile/guess counts from the locked result so
			// the getters report the true TILES/GUESSES (not 1/16 and 0/16) and
			// the share grid matches, even cross-device or after a storage loss
			if (locked) {

				state.revealed = clampTiles(locked.tiles)
				state.picks = (locked.picks || []).slice()

			} else {

				state.picks = []
				state.revealed = 1

			}

		},

		// rehydrate from a saved snapshot, keeping the freshly-loaded puzzle
		// payload (image / number / date) the server just returned
		restore: function(state, data) {

			data = data || {}

			var saved = data.saved || {}
			// a server-locked board takes precedence over the local snapshot,
			// which may pre-date the finish (e.g. solved on another device)
			var locked = data.result || null

			state.today = data.today || null
			state.date = data.date || null
			state.practice = data.practice != null ? !!data.practice : !!saved.practice
			// prefer the freshly-loaded descriptor's token (authoritative)
			state.version = (data.today && data.today.version) || saved.version || null
			state.hints = saved.hints || []
			// the server-locked result (authoritative) wins over a local snapshot,
			// which can be stale (e.g. saved before a fix, or for an old answer)
			state.result = locked || saved.result || null

			if (locked) {

				// seed the real tile/guess counts so the getters and share grid
				// report the finished numbers, not a stale in-progress snapshot
				state.revealed = clampTiles(locked.tiles)
				state.picks = (locked.picks || []).slice()

			} else {

				state.picks = saved.picks || []
				state.revealed = saved.revealed || 1

			}

		},

		pick: function(state, gameId) {

			state.picks.push(gameId)

		},

		// a wrong guess reveals exactly one more tile, capped at 16
		reveal: function(state) {

			state.revealed = Math.min(state.revealed + 1, MAX_TILES)

		},

		hint: function(state, hint) {

			if (hint) state.hints.push(hint)

		},

		result: function(state, result) {

			state.result = result || null

		}

	},

	actions: {

		/*
			Load today's puzzle (or a past date for practice). Resumes saved
			progress when present, else starts fresh. If the server reports the
			board is already locked (`today.locked`), seed the result from it.
		*/
		load: function(context, date) {

			var practice = !!date

			// drop the previous puzzle immediately so the loader shows (and a
			// stale puzzle never lingers when switching from the archive)
			context.commit('clear')
			context.commit('loading', true)

			var endpoint = practice ? 'puzzle' : 'today'
			var params = practice ? { date: date } : {}

			return api.request(endpoint, params).then(function(json) {

				context.commit('loading', false)

				if (!json || json.error) return json

				var puzzleDate = json.date || date || null
				var locked = json.locked || null
				var saved = puzzleDate ? restore(puzzleDate) : null

				// a re-curated date changes the puzzle identity token; a snapshot
				// saved under the OLD token is stale (it may even hold a finished
				// board for the previous game), so drop it and start the new puzzle.
				// A legacy snapshot with no token (saved.version undefined) is also
				// treated as stale once the server starts sending a token.
				if (saved && json.version && saved.version !== json.version) {

					saved = null

				}

				if (saved) {

					context.commit('restore', {
						today: json,
						date: puzzleDate,
						practice: practice,
						saved: saved,
						result: locked
					})

				} else {

					context.commit('init', {
						today: json,
						date: puzzleDate,
						practice: practice,
						result: locked
					})

				}

				// a server-locked board (already solved/lost) persists too, so a
				// reload after the daily reset doesn't re-fetch into a fresh game
				if (locked) persist(context.state)

				return json

			})

		},

		/*
			Submit a guess. We send the running picks (the picks made BEFORE this
			one) so the server can validate position / award hints deterministically.
			On the response:
			- wrong  -> reveal one more tile.
			- hint   -> store the server-authored hint object.
			- solved/lost -> set the result (with the revealed answer) and lock.
			Progress is persisted to localStorage after every guess.
		*/
		guess: function(context, payload) {

			var state = context.state

			// payload may be a bare game id (a normal guess) or { id, skip }.
			// A skip burns a guess and reveals a tile without naming a game; it
			// is stored as id 0 and the server is told via the `skip` flag.
			var skip = !!(payload && typeof payload === 'object' && payload.skip)
			var gameId = skip ? 0 : ((payload && typeof payload === 'object') ? payload.id : payload)

			// board already locked (finished) — ignore further guesses
			if (state.result) return Promise.resolve(state.result)

			// picks BEFORE this guess, for server-side validation
			var previous = state.picks.slice()

			context.commit('pick', gameId)
			context.commit('loading', true)

			return api.request('guess', {
				date: state.date,
				game_id: gameId,
				picks: previous,
				skip: skip
			}).then(function(json) {

				context.commit('loading', false)

				json = json || {}

				if (json.error) return json

				if (!json.correct) context.commit('reveal')

				if (json.hint) context.commit('hint', json.hint)

				if (json.solved || json.lost) {

					context.commit('result', {
						solved: !!json.solved,
						lost: !!json.lost,
						answer: json.answer || null,
						score: json.score != null ? json.score : null
					})

				}

				persist(context.state)

				return json

			})

		}

	}

}
